import { promises as fs } from "node:fs";
// supabase-pack-downloader.ts
//
// v3: Tải team-ai-pack qua Supabase Edge Function thay git submodule.
//
// Flow:
//   1. getValidIdToken() — id_token NAL còn hạn (tự refresh)
//   2. fetchSignedPackUrl(idToken, version?) — POST get-pack → signed URL (300s)
//   3. downloadAndExtractPack(signedUrl, packDir) — tải tarball vào temp TRONG
//      .claude/ (cùng filesystem để rename atomic) → giải nén temp → swap vào packDir
//   4. caller ghi manifest (pack-manifest-store)
//
// Endpoint resolve 3 tầng (dừng ở giá trị đầu tiên có): (a) env
// AVATAR_GET_PACK_ENDPOINT → (b) <workspace>/.claude/settings.json
// env.AVATAR_GET_PACK_ENDPOINT (tiện switch dev/prod theo workspace) → (c)
// DEFAULT_GET_PACK_ENDPOINT (prod). Mọi giá trị đều qua gate HTTPS+whitelist.
import { join } from "node:path";
import {
  atomicSwapDir,
  computeFileSha256,
  downloadFile,
  ensureDir,
  extractTarballToDir,
  removeRecursive,
} from "./filesystem-helpers.js";
import { readWorkspaceGetPackEndpointOverride } from "./read-workspace-get-pack-endpoint-override.js";
import { assertAllowedEndpointHost } from "./validate-endpoint-host-allowlist.js";

const DEFAULT_GET_PACK_ENDPOINT = "https://qpdbkewtpkkbcrptnlos.supabase.co/functions/v1/get-pack";
const FETCH_TIMEOUT_MS = 20_000; // 20s — get-pack chỉ verify token + sign URL, nhanh.

// Host được phép cho endpoint get-pack (gồm cả env override). id_token NAL chỉ
// được gửi tới các host này — chặn AVATAR_GET_PACK_ENDPOINT=https://evil.com
// harvest token. Supabase project host phải EXACT MATCH (subdomain khác của
// *.supabase.co có thể là project của attacker) — domain NAL thì trust cả
// subdomain (suffix) vì NAL kiểm soát toàn bộ 2 domain Workspace.
const ALLOWED_GET_PACK_HOSTS = {
  exactHosts: ["qpdbkewtpkkbcrptnlos.supabase.co"],
  suffixHosts: [".nal.vn", ".nal-software.com"],
};

export function getPackEndpoint(): string {
  // Resolve 3 tầng, dừng ở giá trị đầu tiên có. settings.json là nguồn ÍT tin cậy
  // hơn env nên vẫn BẮT BUỘC qua gate HTTPS+whitelist bên dưới.
  const endpoint =
    process.env.AVATAR_GET_PACK_ENDPOINT ??
    readWorkspaceGetPackEndpointOverride() ??
    DEFAULT_GET_PACK_ENDPOINT;
  // SECURITY: endpoint CHỈ chấp nhận HTTPS + domain whitelist. Chặn kịch bản
  // CI/env injection hoặc settings.json set endpoint lạ để harvest id_token
  // (token gửi trong request).
  let parsed: URL;
  try {
    parsed = new URL(endpoint);
  } catch {
    throw new PackParseError(`AVATAR_GET_PACK_ENDPOINT không phải URL hợp lệ: ${endpoint}`);
  }
  if (parsed.protocol !== "https:") {
    throw new PackParseError(
      `AVATAR_GET_PACK_ENDPOINT phải dùng HTTPS (nhận: ${parsed.protocol}). Từ chối gửi token qua kết nối không mã hóa.`,
    );
  }
  assertAllowedEndpointHost(
    parsed.hostname,
    ALLOWED_GET_PACK_HOSTS,
    (message) => new PackParseError(`AVATAR_GET_PACK_ENDPOINT ${message}`),
  );
  return endpoint;
}

// ─── Error types — caller phân biệt để recovery đúng ─────────────────────────

// id_token sai / hết hạn / email không @nal.vn → KHÔNG retry, prompt avatar login.
export class InvalidIdTokenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidIdTokenError";
  }
}

// Network: timeout / 5xx / DNS / signed URL hết hạn → CÓ thể retry.
export class PackNetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PackNetworkError";
  }
}

// Response lạ / tarball hỏng / sha mismatch → không retry mù.
export class PackParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PackParseError";
  }
}

export interface SignedPackInfo {
  url: string; // signed URL tải tarball (hết hạn ~300s)
  object: string; // vd "pack-v0.2.0.tar.gz"
  expiresIn: number;
  requestedBy: string; // email NAL đã verify
  sha256?: string; // hex digest tarball (optional — backend cũ chưa trả)
}

// Gọi get-pack lấy signed URL. version undefined → server trả stable mới nhất.
export async function fetchSignedPackUrl(
  idToken: string,
  version?: string,
): Promise<SignedPackInfo> {
  let res: Response;
  // Timeout qua AbortController — tránh CLI treo vô hạn nếu endpoint không phản hồi.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    res = await fetch(getPackEndpoint(), {
      method: "POST",
      // id_token CHỈ gửi qua Authorization header (Bearer) — KHÔNG lặp trong
      // body. Edge Function log thường ghi request body → token trong body có
      // thể lọt vào server log. Body chỉ chứa version (không nhạy cảm).
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ version }),
      signal: controller.signal,
    });
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      throw new PackNetworkError(
        `get-pack quá ${FETCH_TIMEOUT_MS / 1000}s không phản hồi. Thử lại.`,
      );
    }
    throw new PackNetworkError(
      `Không kết nối được Supabase get-pack: ${err instanceof Error ? err.message : err}`,
    );
  } finally {
    clearTimeout(timer);
  }

  // 403 = token/domain reject → InvalidIdToken. 5xx = server → Network.
  if (res.status === 401 || res.status === 403) {
    const detail = await safeErrorText(res);
    throw new InvalidIdTokenError(
      `Supabase từ chối (${res.status}): ${detail}. Chạy 'avatar login' với tài khoản @nal.vn.`,
    );
  }
  if (res.status >= 500) {
    throw new PackNetworkError(`Supabase get-pack lỗi server (${res.status}). Thử lại sau.`);
  }
  if (!res.ok) {
    const detail = await safeErrorText(res);
    throw new PackParseError(`get-pack trả ${res.status}: ${detail}`);
  }

  let body: {
    url?: string;
    object?: string;
    expires_in?: number;
    requested_by?: string;
    sha256?: string;
    error?: string;
  };
  try {
    body = (await res.json()) as typeof body;
  } catch {
    throw new PackParseError("get-pack trả response không phải JSON.");
  }
  if (body.error) {
    throw new PackParseError(`get-pack báo lỗi: ${body.error}`);
  }
  if (!body.url || !body.object) {
    throw new PackParseError("get-pack thiếu url/object trong response.");
  }
  return {
    url: body.url,
    object: body.object,
    expiresIn: body.expires_in ?? 0,
    requestedBy: body.requested_by ?? "",
    sha256: body.sha256,
  };
}

// Rác build/cache — KHÔNG bao giờ là nội dung pack hợp lệ ở BẤT KỲ độ sâu nào.
// Xóa ở MỌI độ sâu (vd __pycache__ nằm sâu trong scripts/).
const NESTED_ARTIFACT_NAMES = new Set([".pytest_cache", ".ruff_cache", "__pycache__", ".serena"]);

// Tên thư mục dev/nội bộ CHỈ xóa ở TOP-LEVEL. #102: các tên này TRÙNG với nội dung
// pack hợp lệ nằm sâu — vd `commands/avatar/docs/` (3 slash command /avatar:docs:*)
// và `tools/telemetry/docs/opt-out.md`. Xóa theo tên ở mọi độ sâu sẽ nuốt luôn
// chúng dù tarball tải về có đủ. Khâu build đã loại 2 thư mục này ở top-level; đây
// là lớp phòng thủ thứ 2 cho tarball cũ/build tay sai (vd rơi debate-review v1.0.19).
const TOPLEVEL_ONLY_NAMES = new Set(["plans", "docs"]);

// Xóa artifact dev/nội bộ trong thư mục pack vừa giải nén (đệ quy).
// - `TOPLEVEL_ONLY_NAMES` (plans/docs): CHỈ ở depth 0 — tránh prune nhầm docs lồng sâu (#102).
// - `NESTED_ARTIFACT_NAMES` (cache): ở MỌI độ sâu.
// Idempotent, bỏ qua lỗi từng mục để không chặn sync. `depth` là tham số nội bộ
// (mặc định 0) — KHÔNG đổi API công khai `pruneNonRuntimeArtifacts(root)` đang test dùng.
// Exported để test; gọi nội bộ trong downloadAndExtractPack.
export async function pruneNonRuntimeArtifacts(root: string, depth = 0): Promise<void> {
  let entries: import("node:fs").Dirent[];
  try {
    entries = await fs.readdir(root, { withFileTypes: true });
  } catch {
    return;
  }
  for (const entry of entries) {
    const full = join(root, entry.name);
    const shouldPrune =
      NESTED_ARTIFACT_NAMES.has(entry.name) || (depth === 0 && TOPLEVEL_ONLY_NAMES.has(entry.name));
    if (shouldPrune) {
      await removeRecursive(full).catch(() => {});
    } else if (entry.isDirectory()) {
      await pruneNonRuntimeArtifacts(full, depth + 1); // đệ quy bắt cache nằm sâu
    }
  }
}

// Tải tarball + giải nén vào packDir, atomic swap để không hỏng pack đang có.
//
// Đặt temp TRONG thư mục cha của packDir (cùng filesystem) để rename atomic.
// Bước: tải tarball → giải nén ra <packDir>.new → xóa packDir cũ → rename .new → packDir.
export async function downloadAndExtractPack(
  signedUrl: string,
  packDir: string,
  expectedSha?: string,
): Promise<void> {
  const parent = join(packDir, "..");
  await ensureDir(parent);

  const tmpTarball = join(parent, `.pack-download-${process.pid}.tar.gz`);
  const tmpExtractDir = `${packDir}.new-${process.pid}`;

  try {
    // 1. Tải tarball.
    try {
      await downloadFile(signedUrl, tmpTarball);
    } catch (err) {
      throw new PackNetworkError(
        `Tải tarball thất bại: ${err instanceof Error ? err.message : err}`,
      );
    }

    // 1b. Verify SHA-256 (supply-chain integrity, soft-verify D2).
    // Backend trả expectedSha → so digest tarball đã tải. Mismatch = tarball
    // bị sửa/hỏng → throw, KHÔNG extract. Backend CHƯA deploy (expectedSha
    // undefined) → warn + tiếp tục (backward-compat, không phá pack install prod).
    if (expectedSha) {
      const actualSha = await computeFileSha256(tmpTarball);
      if (actualSha !== expectedSha) {
        throw new PackParseError(
          `SHA-256 tarball không khớp (expected ${expectedSha.slice(0, 12)}…, got ${actualSha.slice(0, 12)}…). Tarball có thể bị sửa đổi — từ chối cài.`,
        );
      }
    } else {
      console.warn(
        "[avatar] Cảnh báo: backend chưa trả SHA-256 digest — bỏ qua verify integrity (pack vẫn cài). Sẽ bật khi backend cập nhật.",
      );
    }

    // 2. Giải nén vào temp dir.
    await removeRecursive(tmpExtractDir);
    try {
      await extractTarballToDir(tmpTarball, tmpExtractDir);
    } catch (err) {
      throw new PackParseError(
        `Giải nén tarball thất bại: ${err instanceof Error ? err.message : err}`,
      );
    }

    // 2b. Dọn artifact dev/nội bộ lỡ lọt vào tarball (defense-in-depth).
    // Script release đã exclude, nhưng tarball cũ/lỗi có thể còn — dọn trên
    // temp dir trước khi swap để pack ở máy user luôn sạch.
    await pruneNonRuntimeArtifacts(tmpExtractDir);

    // 3. Swap an toàn (atomic, rollback on failure) — shared helper.
    await atomicSwapDir(tmpExtractDir, packDir);
  } finally {
    // Dọn temp (tarball + extract dir nếu rename fail).
    await fs.rm(tmpTarball, { force: true }).catch(() => {});
    await removeRecursive(tmpExtractDir).catch(() => {});
  }
}

async function safeErrorText(res: Response): Promise<string> {
  try {
    const body = await res.json();
    return (body as { error?: string }).error ?? JSON.stringify(body).slice(0, 200);
  } catch {
    return (await res.text().catch(() => "")).slice(0, 200);
  }
}
